import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { key: '90d', label: '90D', days: 90 },
  { key: '1y', label: '1Y', days: 365 },
  { key: 'all', label: 'All', days: null },
];

function toneColor(score) {
  if (score >= 85) return '#059669';
  if (score >= 70) return '#65a30d';
  if (score >= 50) return '#d97706';
  return '#e11d48';
}

function fmtDate(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

// Sep 2026 — Portfolio Health Score trend. `portfolioId` null = all
// portfolios combined. Past points are rebuilt from the buy/sell ledger
// using today's fundamentals; today's score is recorded going forward.
export default function HealthScoreTrend({ portfolioId = null, testIdPrefix = 'health-trend' }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('90d');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener('portfolio-changed', bump);
    return () => window.removeEventListener('portfolio-changed', bump);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/portfolios/health-score-history`, { params: portfolioId ? { portfolio_id: portfolioId } : {}, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ points: [] }); });
    return () => { cancelled = true; };
  }, [portfolioId, tick]);

  const points = data?.points || [];
  const visible = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    if (!r?.days) return points;
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - r.days);
    const iso = cutoff.toISOString().slice(0, 10);
    return points.filter((p) => p.date >= iso);
  }, [points, range]);

  if (!data || points.length < 2) return null;

  const first = visible[0];
  const last = visible[visible.length - 1];
  const delta = first && last ? last.score - first.score : 0;
  const deltaTone = delta > 0 ? 'text-emerald-700 dark:text-emerald-400' : delta < 0 ? 'text-rose-700 dark:text-rose-400' : 'text-muted-foreground';
  const recordedFrom = data.recorded_from;
  const oldest = points[0]?.date;

  return (
    <div className="mt-2 rounded-md border border-border/50 bg-background/50 px-2.5 py-2" data-testid={`${testIdPrefix}-trend`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <TrendingUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Health Score Trend</span>
          {visible.length >= 2 && (
            <span className={`text-[10px] font-mono font-semibold ${deltaTone}`} data-testid={`${testIdPrefix}-trend-delta`}>
              {delta > 0 ? '+' : ''}{delta} pts since {fmtDate(first.date)}
            </span>
          )}
        </div>
        <div className="inline-flex rounded-md border border-border/60 overflow-hidden" role="tablist">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`px-2 py-0.5 text-[10px] font-bold transition-colors ${range === r.key ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`${testIdPrefix}-trend-range-${r.key}`}
              aria-selected={range === r.key}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {visible.length < 2 ? (
        <p className="text-[10px] text-muted-foreground py-4 text-center">Not enough points in this window yet — try a wider range.</p>
      ) : (
        <div className="h-28 w-full" data-testid={`${testIdPrefix}-trend-chart`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={visible} margin={{ top: 6, right: 8, bottom: 0, left: -22 }}>
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 9 }} minTickGap={40} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} ticks={[0, 50, 70, 85, 100]} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
              <ReferenceLine y={70} stroke="#65a30d" strokeDasharray="3 3" strokeOpacity={0.5} />
              <ReferenceLine y={50} stroke="#d97706" strokeDasharray="3 3" strokeOpacity={0.5} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }}
                labelFormatter={(v) => fmtDate(v)}
                formatter={(v, _n, item) => [`${v} / 100 · ${item?.payload?.verdict}${item?.payload?.source === 'rebuilt' ? ' (rebuilt)' : ''}`, 'Score']}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke={toneColor(last.score)}
                strokeWidth={2}
                dot={(p) => (p.payload.source === 'recorded'
                  ? <circle key={p.key} cx={p.cx} cy={p.cy} r={2.5} fill={toneColor(p.payload.score)} />
                  : <circle key={p.key} cx={p.cx} cy={p.cy} r={1.5} fill="none" stroke={toneColor(p.payload.score)} />)}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground/80 leading-snug mt-1" data-testid={`${testIdPrefix}-trend-caption`}>
        Solid dots are scores recorded daily{recordedFrom ? ` since ${fmtDate(recordedFrom)}` : ''}. Hollow dots{oldest ? ` (back to ${fmtDate(oldest)})` : ''} are rebuilt from your buy / sell history — the shares you actually held on each date, graded with today&apos;s fundamentals — so they show how your position changes moved conviction, not how each company&apos;s numbers changed back then.
      </p>
    </div>
  );
}
